/**
 * Structured API error class consumed by TanStack Query and feature hooks.
 *
 * The shape mirrors the legacy `static/admin/js/api.js` contract so the
 * frontend can keep displaying server-issued messages and codes without
 * additional translation layers. Four sources are merged into the final
 * `message`, in this order of preference:
 *
 *   1. `payload.message` — the human-readable text the backend returns in
 *      `ErrorResponse.message`. Pulado só quando o código é
 *      `VALIDATION_ERROR` (aí é literal fixo em inglês).
 *   2. `payload.userMessage` — PT-BR do catálogo estático por código.
 *   3. `payload.error`   — alternative key used by Spring's default
 *      `DefaultErrorAttributes` envelope.
 *   4. `fallback`        — a synthetic last-resort string (e.g.,
 *      `"HTTP 503"` or `"Session expired"`) so callers never see an empty
 *      `Error.message`.
 *
 * Why a class and not a discriminated union: TanStack Query checks
 * `instanceof Error` when deciding whether to bubble an exception, and
 * `error.message` flows directly into devtools. A class also gives feature
 * code a single `e instanceof ApiError` predicate.
 */

/** Subset of the standard backend `ErrorResponse` envelope we rely on. */
export interface ApiErrorPayload {
  /**
   * PT-BR fallback do backend (`ErrorResponse.userMessage`, BACK-172). Vem de um
   * catálogo ESTÁTICO por código (`ErrorMessageCatalog`), então é sempre genérico —
   * só usamos quando `message` falta ou é o literal em inglês da validação.
   */
  userMessage?: string;
  /** Motivo específico devolvido pela API — preferido (é o que MER-27 pede). */
  message?: string;
  /** Spring's default error key — used when `message` is absent. */
  error?: string;
  /** Stable machine-readable identifier (e.g. `CANCEL_WINDOW_EXPIRED`). */
  code?: string;
  /** Optional structured context (validation errors, conflicting ids, ...). */
  details?: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | null;
  readonly details: unknown;

  constructor(status: number, payload: ApiErrorPayload, fallback: string) {
    // `message` carrega o motivo específico (ex. "fixedOff não pode ser maior que o
    // preço do produto (R$6.50)") e vence. A ÚNICA exceção é VALIDATION_ERROR, onde o
    // backend manda literal fixo em inglês ("Validation failed"/"Constraint violation").
    const specific = payload.code === 'VALIDATION_ERROR' ? undefined : payload.message;
    super(specific ?? payload.userMessage ?? payload.message ?? payload.error ?? fallback);
    // Native subclasses of `Error` need this for `instanceof` to behave
    // consistently after TS down-levels the constructor in older targets.
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = 'ApiError';
    this.status = status;
    this.code = payload.code ?? null;
    this.details = payload.details ?? null;
  }
}

/**
 * Type guard for narrowing arbitrary thrown values to `ApiError`. Use this
 * instead of bare `instanceof` when interacting with the `unknown` errors
 * surfaced by TanStack Query: it makes intent explicit and survives bundler
 * boundaries that occasionally break prototype chains.
 */
export const isApiError = (e: unknown): e is ApiError => e instanceof ApiError;
